/**
 * Octane Lending Lifecycle Events. "Events" are actually constructor functions, which allows defining required parameters.
 *
 * !!! NOTE !!!: Add all custom Events to the CustomEventMap at the top of this file
 *
 */

import { PageTypes } from './platform';
import Asset from './asset/asset';
import PowersportsAsset from './asset/powersports-asset';
import AssetWrapper from './asset/wrappers';
import { CtaType } from './cta';

/**
 * Custom Event Map that allows us to map the string representation of an event to the Event Class.
 * This allows tab-completion of event names, and TS will know event properties
 */
export interface CustomEventMap {
  'ol-warning': OlWarning;
  'ol-error': OlError;
  'page-type-determined': PageTypeDetermined;
  'start-prequal': StartPrequal;
  'page-updated': PageUpdated;
  'asset-updated': AssetUpdated;
  'asset-wrappers-loaded': AssetWrappersLoaded;
}

/**
 * CustomEventInit Interface where the detail object is required instead of optional
 */
interface OlEventInitRequired<T> extends EventInit {
  detail: T;
}

/**
 * A non-fatal error occurred that should be logged to datadog.
 */
export class OlWarning<T = { error?: any; msg?: string; [key: string]: any }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('ol-warning', eventInitDict);
  }
}

/**
 * A fatal error occurred that should be logged to datadog. This should stop the flow of the OL Widget.
 */
export class OlError<T = { error?: any; msg?: string; [key: string]: any }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('ol-error', eventInitDict);
  }
}

/**
 * Type of page was determined. Ex, Search Results Page [SRP] or Vehicle Detail Page [VDP]
 */
export class PageTypeDetermined<T = { pageType: PageTypes }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('page-type-determined', eventInitDict);
  }
}

/**
 * The page container DOM was changed.
 */
export class PageUpdated<T = { mutations: MutationRecord[] }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('page-updated', eventInitDict);
  }
}

/**
 * The Asset within a container was changed.
 */
export class AssetUpdated<A extends Asset = Asset, T = { asset: A; wrapper: AssetWrapper }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('asset-updated', eventInitDict);
  }
}

/**
 * Type of page was determined. Ex, Search Results Page [SRP] or Vehicle Detail Page [VDP]
 */
export class StartPrequal<
  A extends Asset = PowersportsAsset,
  T = { asset: A; cta_type: CtaType }
> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('start-prequal', eventInitDict);
  }
}

/**
 * All asset wrappers loaded/i.e. the CTAs are prepared
 */
export class AssetWrappersLoaded<T = { assetWrappers: AssetWrapper[]; pageType: PageTypes }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('asset-wrappers-loaded', eventInitDict);
  }
}

/**
 * A JQuery event was triggered. A custom event is used to pass these around for consistency
 */
export class jQueryEvent<T = { $event: JQuery.TriggeredEvent }> extends CustomEvent<T> {
  constructor(eventInitDict: OlEventInitRequired<T>) {
    super('jquery-event', eventInitDict);
  }
}
